<script setup lang="ts">
import { CheckCircleOutlined, PlusOutlined } from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
  createInvoice,
  SUBSCRIPTION_CURRENCY,
  SUBSCRIPTION_PRICE,
  uploadReceipt,
} from '@/services/invoices'
import { fetchActivePaymentModes } from '@/services/paymentModes'
import { validateVoucher } from '@/services/vouchers'
import { useAuthStore } from '@/stores/auth'

const auth = useAuthStore()
const router = useRouter()
const showHowToPay = ref(false)
const paymentModes = ref<
  Array<{
    type: string
    label: string
    account_name: string
    account_number: string
    qr_url?: string | null
  }>
>([])
const activeAddons = ref<Set<string>>(new Set(['Calendar Sync', 'Inventory']))
const showInfo = ref(false)
const infoAddon = ref<any>(null)
const tvScreenCount = ref(1)

const addonDetails: Record<
  string,
  { why: string; infra: string; features: string[]; maintenance: string; requirements?: string }
> = {
  'Calendar Sync': {
    features: [
      'Google Calendar two-way sync',
      'Airbnb iCal import (see Airbnb bookings)',
      'Booking.com iCal import',
      'Unified calendar — all bookings in one view',
      'Per-user connection (each staff can connect)',
    ],
    why: 'Monthly because Google OAuth tokens expire hourly and iCal feeds are polled every hour. Real recurring infrastructure per booking.',
    infra:
      'Vercel serverless function + Google Calendar API + iCal feed polling. OTA sync included at no extra cost.',
    maintenance: 'Included in your monthly subscription. No extra charges for updates or support.',
  },
  Inventory: {
    features: [
      'Track consumables per property',
      'Set low-stock thresholds and alerts',
      'Audit trail of stock changes',
      'Per-room asset checklists',
    ],
    why: 'Monthly because inventory tracking is an ongoing system — adding items, updating stock levels, sending low-stock alerts. You pay for continuous monitoring.',
    infra: 'No external API cost. Pure CasaMana feature with DB queries.',
    maintenance: 'Included in your monthly subscription. No extra charges for updates or support.',
  },
  'FB Conversions API': {
    features: [
      'Track Facebook ad → booking conversions',
      'See exactly which ads bring bookings',
      'Measure ROAS (return on ad spend)',
      'Automatic event firing on confirmed bookings',
    ],
    why: "One-time because it's fire-and-forget. You connect your Facebook Pixel once — every future booking just fires an event to Meta. Zero ongoing cost for us.",
    infra: 'Vercel serverless function call per booking. Meta Conversions API is free.',
    maintenance:
      'Updates or reconfiguration after initial setup: ₱199 per request. Minor fixes included for 6 months after purchase.',
    requirements: 'Requires a Facebook Business Page with an active Pixel.',
  },
  'TV Screens': {
    features: [
      'Hotel-style welcome displays',
      'Digital signage for lobbies and rooms',
      'Per-screen content management',
      'Announcement and ad playlist support',
    ],
    why: 'One-time because the display app runs directly on your existing Android TV. No recurring server costs — just your content delivered to the screen.',
    infra:
      'Runs via our dedicated display app on Android TV. Uses existing CasaMana DB. No external API.',
    maintenance:
      'Updates or reconfiguration: ₱199 per screen. Hardware troubleshooting or on-site revisit: ₱499 per visit.',
  },
  'FB Messenger Bot': {
    features: [
      'Auto-reply to Facebook messages',
      'Answers availability and pricing questions',
      'Creates bookings directly from chat',
      'Escalates to owner when needed',
    ],
    why: 'One-time because the bot auto-replies forever after deployment. Vercel webhook receives messages and queries your booking DB — costs are near-zero per message.',
    infra: 'Vercel serverless webhook + Facebook Send API (free).',
    maintenance:
      'Bot retraining or adding new reply patterns: ₱299 per update. Bug fixes included for 6 months after purchase.',
    requirements: 'Requires an active Facebook Page.',
  },
  'Landing Page': {
    features: [
      'Public property showcase page',
      'Photo gallery with lightbox',
      'Amenities and location details',
      'SEO optimized for search engines',
    ],
    why: "One-time because it's a static page served from Cloudflare R2. No recurring API calls, no server processing. Buy once, host forever.",
    infra: 'Cloudflare R2 storage (near-zero cost). No server needed.',
    maintenance:
      'Content updates (photos, rates, amenities): ₱299 per update. Design changes: ₱499 per revision. Minor fixes included for 6 months.',
  },
  'Complete Stack': {
    features: [
      'FB Conversions API (₱499)',
      'Landing Page (₱1,499)',
      'FB Messenger Bot (₱1,999)',
      'TV Screens — 1 screen (₱499)',
      'Save ₱997 vs buying individually',
    ],
    why: 'One-time because all included add-ons are one-time purchases. You save ₱997 compared to buying individually.',
    infra:
      'Bundle price covers initial setup of all four add-ons. Same maintenance terms as individual add-ons.',
    maintenance:
      '6 months free minor fixes on all items — same as individual add-ons. Standard per-add-on rates apply after.',
  },
}

const openInfo = (addon: any) => {
  infoAddon.value = addon
  tvScreenCount.value = 1
  showInfo.value = true
}

const receiptFile = ref<File | null>(null)
const referenceNumber = ref('')
const uploading = ref(false)
const voucherCode = ref('')
const voucherDiscount = ref<number | null>(null)
const voucherError = ref('')
const voucherValidating = ref(false)

const handleFileChange = (file: File) => {
  receiptFile.value = file
  return false
}

const previewUrl = computed(() =>
  receiptFile.value ? URL.createObjectURL(receiptFile.value) : null,
)

const applyVoucher = async () => {
  if (!voucherCode.value.trim()) {
    voucherError.value = 'Enter a voucher code'
    voucherDiscount.value = null
    return
  }
  voucherValidating.value = true
  voucherError.value = ''
  try {
    const result = await validateVoucher(voucherCode.value.trim(), monthlyTotal.value)
    if (!result.valid) {
      voucherError.value = result.error || 'Invalid voucher'
      voucherDiscount.value = null
    } else {
      voucherDiscount.value = result.discountAmount || 0
      message.success(`Voucher applied: -₱${voucherDiscount.value}`)
    }
  } catch (e: any) {
    voucherError.value = e?.message || 'Invalid voucher'
    voucherDiscount.value = null
  } finally {
    voucherValidating.value = false
  }
}

const clearVoucher = () => {
  voucherCode.value = ''
  voucherDiscount.value = null
  voucherError.value = ''
}

const handleUpload = async () => {
  if (!receiptFile.value || !referenceNumber.value.trim()) {
    message.warning('Select a file and enter reference number')
    return
  }
  uploading.value = true
  try {
    const inv = await createInvoice(undefined, voucherCode.value.trim() || undefined)
    await uploadReceipt(inv.id, receiptFile.value, referenceNumber.value.trim())
    message.success('Receipt uploaded. Awaiting verification.')
    showHowToPay.value = false
    receiptFile.value = null
    referenceNumber.value = ''
    clearVoucher()
  } catch (e: any) {
    message.error(e?.message || 'Upload failed')
  } finally {
    uploading.value = false
  }
}

const monthlyTotal = computed(() => {
  let total = 499
  if (activeAddons.value.has('Calendar Sync')) total += 99
  if (activeAddons.value.has('Inventory')) total += 79
  return total
})

const finalTotal = computed(() => {
  if (voucherDiscount.value !== null && voucherDiscount.value > 0) {
    return Math.max(0, monthlyTotal.value - voucherDiscount.value)
  }
  return monthlyTotal.value
})

const dueDate = computed(() => {
  const d = auth.user?.subscription_end_date
  if (!d) return null
  return new Date(d)
})

const daysLeft = computed(() => {
  if (!dueDate.value) return null
  const diff = Math.ceil((dueDate.value.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
  return diff > 0 ? diff : 0
})

const loadPaymentModes = async () => {
  try {
    paymentModes.value = await fetchActivePaymentModes()
  } catch {
    paymentModes.value = []
  }
}

const openPayNow = () => {
  loadPaymentModes()
  showHowToPay.value = true
}
</script>

<template>
  <div style="padding: 24px">
    <h2 style="margin: 0 0 24px">Billing</h2>

    <!-- Plan Card -->
    <a-card style="margin-bottom: 24px">
      <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 16px">
        <div style="flex: 1; min-width: 200px">
          <div style="font-size: 20px; font-weight: 700">Starter Plan</div>
          <div style="font-size: 32px; font-weight: 700; margin: 8px 0">
            ₱{{ monthlyTotal }}
            <span style="font-size: 14px; font-weight: 400; color: var(--text-secondary)">/month</span>
          </div>
          <div style="color: var(--text-secondary); font-size: 12px; line-height: 1.8">
            <div>Starter plan<span style="float: right">₱499</span></div>
            <div v-if="activeAddons.has('Calendar Sync')">Calendar Sync<span style="float: right">₱99</span></div>
            <div v-if="activeAddons.has('Inventory')">Inventory<span style="float: right">₱79</span></div>
          </div>
          <div style="margin-top: 8px">
            <a-tag color="green" style="font-size: 12px"><CheckCircleOutlined /> Active</a-tag>
            <span v-if="dueDate" style="font-size: 12px; color: var(--text-secondary); margin-left: 8px">
              Due {{ dueDate.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' }) }}
              · <strong>{{ daysLeft }} day{{ daysLeft === 1 ? '' : 's' }} left</strong>
            </span>
          </div>
        </div>
        <div style="text-align: right">
          <a-button size="large" @click="router.push('/billing/history')">Payment History</a-button>
          <a-button type="primary" size="large" style="margin-left: 8px" @click="openPayNow">Pay Now</a-button>
        </div>
      </div>
    </a-card>

    <!-- Tier Plans -->
    <a-card title="Plans" style="margin-bottom: 24px">
      <a-row :gutter="[16, 16]" :style="{ alignItems: 'stretch' }">
        <a-col :xs="24" :md="8" :style="{ display: 'flex' }">
          <a-card size="small" style="width: 100%; display: flex; flex-direction: column" :body-style="{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column' }">
            <div style="flex: 1">
            <div style="font-size: 18px; font-weight: 700; margin-bottom: 8px">Starter</div>
            <div style="font-size: 28px; font-weight: 700; margin-bottom: 4px">₱499</div>
            <div style="color: var(--text-secondary); margin-bottom: 12px; font-size: 13px">/month</div>
            <a-tag color="blue">1–3 Properties</a-tag>
            <div style="text-align: left; margin-top: 12px; font-size: 13px; line-height: 2; padding: 0 4px">
              <CheckCircleOutlined style="color: #52c41a; margin-right: 6px" />Bookings & Calendar
              <br /><CheckCircleOutlined style="color: #52c41a; margin-right: 6px" />Guest Manager
              <br /><CheckCircleOutlined style="color: #52c41a; margin-right: 6px" />Dashboard & Reports
              <br /><CheckCircleOutlined style="color: #52c41a; margin-right: 6px" />Properties Manager
            </div>
            </div>
            <a-button block style="margin-top: 12px" disabled>Current Active</a-button>
          </a-card>
        </a-col>
        <a-col :xs="24" :md="8" :style="{ display: 'flex' }">
          <a-card size="small" style="width: 100%; display: flex; flex-direction: column; border: 2px solid #1677ff; border-radius: 8px" :body-style="{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column' }">
            <div style="flex: 1">
            <a-tag color="#1677ff" style="position: absolute; top: -12px; left: 50%; transform: translateX(-50%)">Popular</a-tag>
            <div style="font-size: 18px; font-weight: 700; margin-bottom: 8px; margin-top: 4px">Growth</div>
            <div style="font-size: 28px; font-weight: 700; margin-bottom: 4px">₱999</div>
            <div style="color: var(--text-secondary); margin-bottom: 12px; font-size: 13px">/month</div>
            <a-tag color="purple">4–10 Properties</a-tag>
            <div style="text-align: left; margin-top: 12px; font-size: 13px; line-height: 2; padding: 0 4px">
              <CheckCircleOutlined style="color: #52c41a; margin-right: 6px" />Everything in Starter
              <br /><CheckCircleOutlined style="color: #52c41a; margin-right: 6px" />Calendar Sync <a-tag color="blue" style="font-size: 10px; margin-left: 4px">bundled</a-tag>
            </div>
            </div>
            <a-button type="primary" block style="margin-top: 12px">Upgrade</a-button>
          </a-card>
        </a-col>
        <a-col :xs="24" :md="8" :style="{ display: 'flex' }">
          <a-card size="small" style="width: 100%; display: flex; flex-direction: column" :body-style="{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column' }">
            <div style="flex: 1">
            <div style="font-size: 18px; font-weight: 700; margin-bottom: 8px">Enterprise</div>
            <div style="font-size: 28px; font-weight: 700; margin-bottom: 4px">Custom</div>
            <div style="color: var(--text-secondary); margin-bottom: 12px; font-size: 13px">contact us</div>
            <a-tag color="gold">Unlimited Properties</a-tag>
            <div style="text-align: left; margin-top: 12px; font-size: 13px; line-height: 2; padding: 0 4px">
              <CheckCircleOutlined style="color: #52c41a; margin-right: 6px" />Everything in Growth
              <br /><CheckCircleOutlined style="color: #52c41a; margin-right: 6px" />Priority Support
              <br /><CheckCircleOutlined style="color: #52c41a; margin-right: 6px" />Custom Branding
            </div>
            </div>
            <a-button block style="margin-top: 12px">Contact Us</a-button>
          </a-card>
        </a-col>
      </a-row>
    </a-card>

    <!-- Add-ons -->
    <a-card title="Add-ons" style="margin-bottom: 24px">
      <div style="color: var(--text-secondary); font-size: 13px; margin-bottom: 16px">
        Enhance your plan with optional add-ons. Available for all tiers. One-time add-ons include 6 months of free fixes — maintenance fees apply for updates thereafter.
      </div>

      <a-row :gutter="[16, 16]" style="margin-bottom: 16px">
        <a-col :span="24">
          <a-card size="small" hoverable :body-style="{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }">
            <div style="flex: 1; min-width: 200px">
              <a-tag color="gold" style="margin-bottom: 4px">Best Value</a-tag>
              <div style="font-size: 16px; font-weight: 700; margin-bottom: 4px">Complete Stack</div>
              <div style="font-size: 24px; font-weight: 700">₱3,499 <span style="font-size: 13px; font-weight: 400; color: var(--text-secondary)">one-time</span></div>
              <div style="font-size: 12px; color: #52c41a; margin-top: 2px">Save ₱997</div>
              <div style="font-size: 12px; color: var(--text-secondary); margin-top: 6px; line-height: 1.6">
                All one-time add-ons: FB Conversions + Landing Page + FB Messenger Bot + TV Screens (1 screen)
              </div>
            </div>
            <a-space direction="vertical" style="text-align: right">
              <a-button type="primary" size="large">Get Complete Stack</a-button>
              <a-button type="link" size="small" style="padding: 0" @click="openInfo({ name: 'Complete Stack', price: '₱3,499', badge: 'One-time', period: '' })">More Info</a-button>
            </a-space>
          </a-card>
        </a-col>
      </a-row>

      <a-row :gutter="[16, 16]">
        <a-col :xs="24" :sm="12" :md="8" v-for="addon in [
          { name: 'Inventory', price: '₱79', period: '/mo', badge: 'Monthly', toggle: true, desc: 'Consumables tracking. Low stock alerts, audit trails.' },
          { name: 'Calendar Sync', price: '₱99', period: '/mo', badge: 'Monthly', toggle: true, desc: 'Google Calendar + OTA sync. Airbnb & Booking.com iCal. One view, all bookings.' },
          { name: 'TV Screens', price: '₱499', period: '/screen', badge: 'One-time', toggle: false, desc: 'Hotel-style welcome displays. Android TV required. We set it up.' },
          { name: 'FB Conversions API', price: '₱499', period: '', badge: 'One-time', toggle: false, desc: 'Track Facebook ad → booking conversions. Measure your ad spend ROI.' },
          { name: 'Landing Page', price: '₱1,499', period: '', badge: 'One-time', toggle: false, desc: 'Public property showcase. Photos, amenities, location — all in one page.' },
          { name: 'FB Messenger Bot', price: '₱1,999', period: '', badge: 'One-time', toggle: false, desc: 'Auto-reply bot. Answers availability, pricing, and books guests via Messenger.' },
        ]" :key="addon.name">
          <a-card size="small" :body-style="{ textAlign: 'center' }">
            <a-badge-ribbon v-if="addon.toggle && activeAddons.has(addon.name)" text="Active" color="green" placement="start">
            <a-tag :color="addon.badge === 'Monthly' ? 'blue' : 'green'" style="margin-bottom: 8px">{{ addon.badge }}</a-tag>
            <div style="font-size: 15px; font-weight: 600; margin-bottom: 4px">{{ addon.name }}</div>
            <div style="font-size: 22px; font-weight: 700; margin-bottom: 2px">{{ addon.price }}<span style="font-size: 12px; font-weight: 400; color: var(--text-secondary)">{{ addon.period }}</span></div>
            <div style="font-size: 12px; color: var(--text-secondary); margin-top: 8px; line-height: 1.5">{{ addon.desc }}</div>
            </a-badge-ribbon>
            <template v-else>
            <a-tag :color="addon.badge === 'Monthly' ? 'blue' : 'green'" style="margin-bottom: 8px">{{ addon.badge }}</a-tag>
            <div style="font-size: 15px; font-weight: 600; margin-bottom: 4px">{{ addon.name }}</div>
            <div style="font-size: 22px; font-weight: 700; margin-bottom: 2px">{{ addon.price }}<span style="font-size: 12px; font-weight: 400; color: var(--text-secondary)">{{ addon.period }}</span></div>
            <div style="font-size: 12px; color: var(--text-secondary); margin-top: 8px; line-height: 1.5">{{ addon.desc }}</div>
            </template>
            <a-button
              v-if="addon.toggle"
              size="small"
              :type="activeAddons.has(addon.name) ? 'default' : 'primary'"
              style="margin-top: 8px"
              @click="activeAddons.has(addon.name) ? activeAddons.delete(addon.name) : activeAddons.add(addon.name)"
            >
              {{ activeAddons.has(addon.name) ? 'Deactivate' : 'Activate' }}
            </a-button>
            <a-button
              v-else
              type="primary"
              size="small"
              style="margin-top: 8px"
            >Purchase</a-button>
            <a-button size="small" type="link" style="margin-top: 4px" @click="openInfo(addon)">More Info</a-button>
          </a-card>
        </a-col>
      </a-row>
    </a-card>

    <!-- Payment Methods Modal -->
    <a-modal v-model:open="showHowToPay" title="Payment Methods" :footer="null" width="480px">
      <div v-if="paymentModes.length === 0" style="text-align: center; padding: 16px; color: var(--text-secondary)">
        No payment methods available yet.
      </div>
      <a-list v-else :data-source="paymentModes" :split="true">
        <template #renderItem="{ item }">
          <a-list-item>
            <a-list-item-meta>
              <template #avatar>
                <a-avatar v-if="item.type === 'ewallet'" style="background-color: #007dfe" :size="40">EW</a-avatar>
                <a-avatar v-else-if="item.type === 'bank'" style="background-color: #1677ff" :size="40">BK</a-avatar>
                <a-avatar v-else style="background-color: #722ed1" :size="40">CD</a-avatar>
              </template>
              <template #title>{{ item.label }}</template>
              <template #description>
                <div>{{ item.account_name }}</div>
                <div>{{ item.account_number }}</div>
              </template>
            </a-list-item-meta>
            <a-image
              v-if="item.qr_url"
              :src="item.qr_url"
              :width="80"
              :preview="true"
            />
          </a-list-item>
        </template>
      </a-list>
      <a-divider style="margin: 12px 0" />
      <div style="margin-bottom: 12px; padding: 12px; background: #f6ffed; border: 1px solid #b7eb8f; border-radius: 6px; font-size: 13px">
        <div style="display: flex; justify-content: space-between">
          <span>Total:</span><span>₱{{ monthlyTotal }}</span>
        </div>
        <div v-if="voucherDiscount !== null && voucherDiscount > 0" style="display: flex; justify-content: space-between; color: #52c41a">
          <span>Discount:</span><span>-₱{{ voucherDiscount }}</span>
        </div>
        <div style="display: flex; justify-content: space-between; font-weight: 700; border-top: 1px solid #b7eb8f; margin-top: 6px; padding-top: 6px">
          <span>Final:</span><span>₱{{ finalTotal }}</span>
        </div>
      </div>
      <a-form layout="vertical">
        <a-form-item label="Voucher Code (optional)" :help="voucherError" :validate-status="voucherError ? 'error' : ''">
          <a-space-compact style="width: 100%">
            <a-input v-model:value="voucherCode" placeholder="e.g. PILOT50" style="flex: 1" :disabled="voucherDiscount !== null" />
            <a-button v-if="voucherDiscount === null" :loading="voucherValidating" @click="applyVoucher">Apply</a-button>
            <a-button v-else @click="clearVoucher">Clear</a-button>
          </a-space-compact>
          <div v-if="voucherDiscount !== null" style="color: #52c41a; font-size: 12px; margin-top: 4px">Voucher applied: -₱{{ voucherDiscount }}</div>
        </a-form-item>
        <a-form-item label="Reference Number">
          <a-input v-model:value="referenceNumber" placeholder="e.g. REF-1234567890" />
        </a-form-item>
        <a-form-item label="Receipt Screenshot">
          <a-upload
            :before-upload="handleFileChange"
            :show-upload-list="false"
            accept="image/*"
            list-type="picture-card"
          >
            <img v-if="previewUrl" :src="previewUrl ?? undefined" style="width: 100%; height: 100%; object-fit: cover" />
            <div v-else>
              <PlusOutlined />
              <div style="margin-top: 8px">Upload</div>
            </div>
          </a-upload>
        </a-form-item>
        <a-form-item>
          <a-button type="primary" :loading="uploading" :disabled="!receiptFile || !referenceNumber.trim()" @click="handleUpload" block>
            Upload Receipt
          </a-button>
        </a-form-item>
      </a-form>
      <div style="font-size: 12px; color: var(--text-secondary); text-align: center">
        Your payment will be verified within 24 hours.
      </div>
    </a-modal>

    <!-- Add-on Info Modal -->
    <a-modal v-model:open="showInfo" :title="infoAddon?.name" :footer="null" width="520px">
      <template v-if="infoAddon">
        <template v-if="infoAddon.name === 'Complete Stack'">
          <a-descriptions :column="1" size="small" bordered style="margin-bottom: 16px">
            <a-descriptions-item label="Price">₱3,499 (one-time)</a-descriptions-item>
            <a-descriptions-item label="You save">₱997 (22%)</a-descriptions-item>
          </a-descriptions>

          <a-divider orientation="left" style="font-size: 13px">What's Included</a-divider>
          <a-descriptions :column="1" size="small" bordered>
            <a-descriptions-item label="FB Conversions API">₱499</a-descriptions-item>
            <a-descriptions-item label="Landing Page">₱1,499</a-descriptions-item>
            <a-descriptions-item label="FB Messenger Bot">₱1,999</a-descriptions-item>
            <a-descriptions-item label="TV Screens (1 screen)">₱499</a-descriptions-item>
            <a-descriptions-item label="Individual total">₱4,496</a-descriptions-item>
            <a-descriptions-item label="Bundle price">
              <span style="color: #52c41a; font-weight: 700">₱3,499</span>
            </a-descriptions-item>
          </a-descriptions>

          <a-divider orientation="left" style="font-size: 13px">Why one-time?</a-divider>
          <p style="font-size: 13px; color: var(--text-secondary)">All included add-ons are one-time purchases. Pay once, own forever.</p>

          <a-divider orientation="left" style="font-size: 13px">Maintenance & Support</a-divider>
          <p style="font-size: 13px; color: var(--text-secondary)">6 months free minor fixes — same as individual add-ons. Standard per-add-on rates apply after.</p>

          <a-divider orientation="left" style="font-size: 13px">Additional Screens</a-divider>
          <p style="font-size: 13px; color: var(--text-secondary)">The bundle includes 1 TV screen. Additional screens: ₱499 each, added anytime.</p>
        </template>
        <template v-else>
        <a-descriptions :column="1" size="small" bordered style="margin-bottom: 16px">
          <a-descriptions-item label="Price">{{ infoAddon.price }}<span v-if="infoAddon.period"> {{ infoAddon.period }}</span></a-descriptions-item>
          <a-descriptions-item label="Type">{{ infoAddon.badge }}</a-descriptions-item>
        </a-descriptions>

        <a-divider orientation="left" style="font-size: 13px">Features</a-divider>
        <ul style="margin: 0; padding-left: 20px; font-size: 13px; line-height: 2; color: var(--text-secondary)">
          <li v-for="f in addonDetails[infoAddon.name]?.features" :key="f">{{ f }}</li>
        </ul>

        <a-divider orientation="left" style="font-size: 13px">Why {{ infoAddon.badge === 'Monthly' ? 'monthly' : 'one-time' }}?</a-divider>
        <p style="font-size: 13px; color: var(--text-secondary)">{{ addonDetails[infoAddon.name]?.why }}</p>

        <a-divider orientation="left" style="font-size: 13px">Infrastructure</a-divider>
        <p style="font-size: 13px; color: var(--text-secondary)">{{ addonDetails[infoAddon.name]?.infra }}</p>

        <a-divider orientation="left" style="font-size: 13px">Maintenance & Support</a-divider>
        <p style="font-size: 13px; color: var(--text-secondary)">{{ addonDetails[infoAddon.name]?.maintenance }}</p>

        <template v-if="addonDetails[infoAddon.name]?.requirements">
          <a-divider orientation="left" style="font-size: 13px">Requirements</a-divider>
          <p style="font-size: 13px; color: var(--text-secondary)">{{ addonDetails[infoAddon.name]?.requirements }}</p>
        </template>

        <template v-if="infoAddon.name === 'TV Screens'">
          <a-divider orientation="left" style="font-size: 13px">Calculator</a-divider>
          <a-space direction="vertical" style="width: 100%">
            <div style="display: flex; justify-content: space-between; align-items: center">
              <span>Screens</span>
              <a-input-number v-model:value="tvScreenCount" :min="1" :max="50" size="small" style="width: 80px" />
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center">
              <span>₱499 × {{ tvScreenCount }} screen{{ tvScreenCount > 1 ? 's' : '' }}</span>
              <strong>₱{{ (tvScreenCount * 499).toLocaleString() }}</strong>
            </div>
          </a-space>
          <a-alert type="info" show-icon style="margin-top: 12px; font-size: 12px">
            <template #message>
              <strong>Requirements:</strong> Android TV required (any brand). We handle the setup and configuration for you.
            </template>
          </a-alert>
        </template>
        </template>

        <a-divider style="margin: 16px 0" />
        <div style="text-align: center; font-size: 12px; color: var(--text-secondary)">
          Questions? <a-button type="link" size="small" style="padding: 0; height: auto" @click="showInfo = false; router.push('/about')">Contact us</a-button>
        </div>
      </template>
    </a-modal>
  </div>
</template>
